import { useState, useEffect } from 'react';
import { apiFetch } from './config';

// THE DELEGATE'S DOOR (2026-08-16, DECISIONS.md "A testimony is truth-apt, and
// onward sharing is a grant on an OBJECT").
//
// Not a variant of ThreadShare and deliberately not folded into it. That panel
// is the AUTHOR's: it publishes to everyone, mints secret links, unpublishes,
// and reports staleness. A delegate can do exactly one of those things —
// widen to a circle of their own — and every other control on that panel is
// something they must not have. A shared component with half its buttons
// hidden would be one refactor away from showing them.
//
// The three bounds are enforced server-side and stated here, because a person
// deciding whether to pass a life story on deserves to know what the act does
// before they do it, not after:
//   - to a group they are in, or nobody
//   - never to everyone
//   - and the person they pass it to cannot pass it on again
//
// The two-call ceremony is the same as publish's, for the same reason: sharing
// a thread shares the steps it arranges, and that consequence is easy not to
// think about while clicking a button called share. The audit runs first and
// changes nothing.

const snip = (s, n) => (s && s.length > n ? `${s.slice(0, n)}…` : s || '');

export default function ThreadPassOn({ threadId, onChanged }) {
  const [open, setOpen] = useState(false);
  const [groups, setGroups] = useState([]);
  const [pending, setPending] = useState(null); // { audit, groups }
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);
  const [done, setDone] = useState(null);

  useEffect(() => {
    if (!open) return;
    apiFetch('/api/groups')
      .then((r) => (r.ok ? r.json() : []))
      .then((g) => setGroups(Array.isArray(g) ? g : []))
      .catch(() => {});
  }, [open]);

  const call = async (body) => {
    setBusy(true);
    setError(null);
    try {
      const res = await apiFetch(`/api/threads/${threadId}/publish`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(body),
      });
      const json = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(json.error || 'failed');
      return json;
    } finally {
      setBusy(false);
    }
  };

  const start = async (groupObjectId) => {
    try {
      const audit = await call({ audience: { human: 'group', groups: [groupObjectId] } });
      setPending({ audit, groups: [groupObjectId] });
    } catch (e) { setError(e.message); }
  };

  const confirm = async () => {
    try {
      await call({ audience: { human: 'group', groups: pending.groups }, share_members: true });
      setPending(null);
      setDone('Passed on. The author can see that you did, and to which group.');
      onChanged?.();
    } catch (e) { setError(e.message); }
  };

  if (!open) {
    return (
      <button className="wv-chip" onClick={() => setOpen(true)}>
        the author lets you pass this on…
      </button>
    );
  }

  const members = pending?.audit?.private_members || [];

  return (
    <div className="wv-panel" style={{ marginTop: 'var(--wv-sp-2)', padding: 'var(--wv-sp-3)' }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 'var(--wv-sp-2)' }}>
        <strong style={{ fontSize: 'var(--wv-fs-sm)' }}>Pass this on</strong>
        <button className="wv-chip" onClick={() => { setOpen(false); setPending(null); }}>close</button>
      </div>

      <p className="trust-muted" style={{ fontSize: 'var(--wv-fs-sm)', marginTop: 'var(--wv-sp-1)' }}>
        The author has allowed you to share this with a group you belong to. You cannot make it
        public, and whoever you share it with cannot pass it on again. Your name is recorded against
        the share.
      </p>

      {error && (
        <div style={{ color: 'var(--wv-neg)', fontSize: 'var(--wv-fs-sm)', marginTop: 'var(--wv-sp-1)' }}>{error}</div>
      )}
      {done && (
        <div style={{ fontSize: 'var(--wv-fs-sm)', marginTop: 'var(--wv-sp-1)' }}>{done}</div>
      )}

      {pending ? (
        <div style={{ marginTop: 'var(--wv-sp-2)' }}>
          <p style={{ fontSize: 'var(--wv-fs-sm)', marginBottom: 'var(--wv-sp-2)' }}>{pending.audit.note}</p>
          {members.length > 0 && (
            <ul style={{ marginBottom: 'var(--wv-sp-2)' }}>
              {members.map((m) => (
                <li key={m.id} style={{ fontSize: 'var(--wv-fs-sm)' }}>{snip(m.content, 90)}</li>
              ))}
            </ul>
          )}
          {pending.audit.withheld?.length > 0 && (
            <p className="trust-muted" style={{ fontSize: 'var(--wv-fs-sm)' }}>
              {pending.audit.withheld.length} step
              {pending.audit.withheld.length === 1 ? '' : 's'} you cannot see will not be shared —
              readers will find the thread ends early.
            </p>
          )}
          {pending.audit.nested_threads?.length > 0 && (
            <p className="trust-muted" style={{ fontSize: 'var(--wv-fs-sm)' }}>
              This contains {pending.audit.nested_threads.length} chapter
              {pending.audit.nested_threads.length === 1 ? '' : 's'} — each one is separate, and
              only the author can pass those on unless they have allowed you there too.
            </p>
          )}
          <button className="wv-chip" disabled={busy} onClick={confirm}>share with the group</button>{' '}
          <button className="wv-chip" disabled={busy} onClick={() => setPending(null)}>cancel</button>
        </div>
      ) : (
        <div style={{ marginTop: 'var(--wv-sp-2)', display: 'flex', gap: 'var(--wv-sp-1)', flexWrap: 'wrap' }}>
          {groups.length === 0 ? (
            <span className="trust-muted" style={{ fontSize: 'var(--wv-fs-sm)' }}>
              You are not in any group yet — make one at /groups, then come back.
            </span>
          ) : (
            groups.map((g) => (
              <button key={g.object_id} className="wv-chip" disabled={busy} onClick={() => start(g.object_id)}>
                share with {g.name}…
              </button>
            ))
          )}
        </div>
      )}
    </div>
  );
}
